import React, { useMemo, useState } from 'react';
import PortalPreview from '../portal/composer/PortalPreview';
import StepPick from '../portal/composer/StepPick';
import SendPanel, { sendSummary } from '../portal/composer/SendPanel';
import WindowSheet from '../portal/composer/WindowSheet';
import Sheet, { PILL_BASE, pill, FILL } from '../portal/composer/Sheet';
import { SearchPill, Rows } from '../portal/composer/LinkSheet';
import { XIcon, ChevronLeftIcon, PinOnlyIcon } from '../portal/canvas/icons';
import { normalizeLayout } from '../../utils/portalLayout';
import { FRAME_DEFAULT } from '../../utils/mediaFrame';
import { DEFAULT_WINDOW, windowLabel, windowShort, windowDateBits } from '../../utils/portalWindow';
import ringArt from '../../assets/img/portal-ring.png';
import { PHONE_W, SCREEN_H, MONO } from './landingTokens';

/**
 * The real Portal composer, driven by the landing page's scroll (handoff §4 "See it work ·
 * OPEN A PORTAL"). The page hands it the same state the prototype posted to its iframe —
 * `{ step, mode, photo, text, sheet, venue, winOn, aud, sent }` — and this draws the composer's
 * own pieces for it: the canvas (PortalPreview + the layout editor's chrome), the PICK panel,
 * the venue and window sheets, the SEND panel and the PORTAL SENT. screen. Nothing here is
 * interactive: the phone it sits in has pointer-events off.
 */
export const STEP_LABEL = ['PICK', 'MAKE', 'OPEN'];
export const DEMO_VENUE = { name: 'The Bench Market', address: '368 Vancouver Ave', city: 'Penticton' };
/** The window the demo switches on: live now, gone at 8 PM. */
export const DEMO_WINDOW = Object.freeze({ on: true, day: 0, start: null, endAbs: 20 });
const DEMO_CLUBS = [{ id: 'c1', name: 'Sundown Supper' }, { id: 'c2', name: 'Wine Trivia' }];
const CITIES = [['PENTICTON', null, true], ['NARAMATA', '11 KM'], ['SUMMERLAND', '14 KM'], ['OLIVER', '36 KM'], ['KELOWNA', '48 KM']];
const PLACES = [['The Bench Market', '368 VANCOUVER AVE'], ['Bench 1775 Winery', '1775 NARAMATA RD'], ['Bad Tattoo Brewing', '169 ESTABROOK AVE']];
const noop = () => {};

function DemoVenueSheet({ venue }) {
  const rows = PLACES.map(([title, meta], i) => ({ key: title, title, meta, tag: i === 0 && venue ? 'ADDED' : 'ADD', on: i === 0 && !!venue, pick: noop }));
  return (
    <Sheet title="ADD A VENUE" suffix="· OPTIONAL" onClose={noop} testId="demo-venue-sheet">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
        <span style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', background: FILL, color: '#fff', fontSize: 10, letterSpacing: '0.12em' }}><PinOnlyIcon size={12} />CITY</span>
        {CITIES.map(([c, km, on]) => (
          <span key={c} style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 14px', ...pill(!!on), fontSize: 10, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
            {c}{km && <span style={{ fontSize: 9, letterSpacing: '0.06em', opacity: 0.6, fontWeight: 400 }}>{km}</span>}
          </span>
        ))}
      </div>
      <SearchPill value="bench" onChange={noop} placeholder="Search a place in Penticton…" />
      <Rows rows={rows} />
    </Sheet>
  );
}

function PortalSent({ summary, avatar }) {
  return (
    <div data-testid="demo-sent" style={{ position: 'absolute', inset: 0, zIndex: 20, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center', color: '#fff' }}>
      <span aria-hidden style={{ position: 'relative', display: 'block', width: 120, height: 120, marginBottom: 10 }}>
        <span className="pl-sent-ripple" style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #00FFFF' }} />
        <span className="pl-sent-spin" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: `url(${ringArt}) center / 100% 100% no-repeat` }} />
        <span style={{ position: 'absolute', inset: '5.5%', borderRadius: '50%', background: `#222 url(${avatar}) center / cover` }} />
      </span>
      <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '.18em' }}>PORTAL SENT.</span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: 'rgba(255,255,255,.7)' }}>{summary}</span>
      <span style={{ marginTop: 12, height: 44, padding: '0 22px', display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,.14)', borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: '.14em' }}>MAKE ANOTHER</span>
    </div>
  );
}

export default function ComposerDemo({ demo, sender }) {
  const { step = 1, mode = 'text', photo = null, text = '', sheet = null, venue = null, winOn = false, aud = ['friends'], sent = false } = demo || {};
  const [layout, setLayout] = useState(() => normalizeLayout(null));
  const media = useMemo(() => (mode === 'photo' && photo ? { kind: 'photo', localUrl: photo, file: null, name: 'patio.jpg' } : { kind: 'text', localUrl: null, file: null, name: null }), [mode, photo]);
  const win = winOn ? DEMO_WINDOW : DEFAULT_WINDOW;
  const label = windowLabel(win);
  const venueOnCard = venue ? DEMO_VENUE : null;
  const venueDate = useMemo(() => windowDateBits(win), [win]);
  const summary = sendSummary(aud, 0, label, 'open');

  return (
    <div data-testid="composer-demo" style={{ position: 'relative', width: PHONE_W, height: SCREEN_H, background: '#000', color: '#fff', fontFamily: MONO, overflow: 'hidden', userSelect: 'none', pointerEvents: 'none' }}>
      <PortalPreview
        media={media}
        text={text}
        chip={label}
        layout={layout}
        onLayout={setLayout}
        venue={venueOnCard}
        venueDate={venueDate}
        editor={{ onText: noop, editing: false, onEditing: noop, selected: null, onSelected: noop, onOpen: noop, onRemove: noop, frame: FRAME_DEFAULT, onFrame: null, isVideo: false, windowOn: win.on, locked: step !== 2, showChrome: step === 2 && !sheet, venueOpen: false, onVenueOpen: noop, venueTip: false, onVenueTip: noop, mode: 'open' }}
        frame={FRAME_DEFAULT}
        showHeader={step === 3}
        timeLeft={windowShort(win)}
        authorName={sender.handle}
        authorAvatarUrl={sender.avatar}
        className="absolute inset-0"
      />

      {!sent && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, zIndex: 8, padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="demo-header">
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) 56px', alignItems: 'center' }}>
            <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.5)', borderRadius: '50%' }}>{step === 1 ? <XIcon size={18} /> : <ChevronLeftIcon />}</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.14em' }}>CREATE A PORTAL</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)' }} data-testid="demo-step">{STEP_LABEL[step - 1]} · 0{step} / 03</span>
            </div>
            {step < 3 ? <span style={{ justifySelf: 'end', padding: '8px 0', color: '#00FFFF', fontSize: 14, fontWeight: 700, letterSpacing: '0.14em' }}>NEXT</span> : <span />}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }} aria-hidden>
            {[1, 2, 3].map((n) => <div key={n} style={{ height: 3, borderRadius: 999, background: n <= step ? '#fff' : 'rgba(255,255,255,.3)' }} />)}
          </div>
        </div>
      )}

      {step === 1 && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 6, background: '#000', padding: '18px 16px 24px' }}>
          <StepPick media={media} onPickFile={noop} onPickText={noop} />
        </div>
      )}

      {step === 2 && sheet === 'venue' && <DemoVenueSheet venue={venue} />}
      {step === 2 && sheet === 'win' && <WindowSheet value={win} onChange={noop} onClose={noop} />}

      {step === 3 && !sent && (
        <SendPanel aud={aud} onAud={noop} clubs={DEMO_CLUBS} clubIds={[]} onClubIds={noop} cityLabel="Penticton" onOpenCity={noop} peek={false} onTogglePeek={noop} onSend={noop} windowLabel={label} joinMode="open" onJoinMode={noop} />
      )}

      {sent && <PortalSent summary={summary} avatar={sender.avatar} />}
    </div>
  );
}
